import { TranscriptAnswerService } from '@src/services/transcriptAnswer/TranscriptAnswerService';
import { useMemo } from 'react';

import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';
import type { RootStore } from '@src/stores-v2/RootStore';

/**
 * DI seam for `TranscriptAnswerService`, mirroring `useClipSearchService`.
 *
 * The instance is memoized on its deps so the identity is stable across
 * renders — a fresh service every render would re-fire any effect that lists
 * it (the hook-mock trap documented in CLAUDE.md → Testing).
 *
 * The context is passed as a **getter**, not a snapshot, so the memo can hold
 * while the story or identity underneath it changes.
 */
export function useTranscriptAnswerService(
  videoCreator: IVideoCreatorStore,
  rootStore: RootStore,
): TranscriptAnswerService {
  return useMemo(
    () =>
      new TranscriptAnswerService({
        promptRepository: rootStore.datoClientStore.aiPromptRepository,
        getContext: () => ({
          storyId: videoCreator.story?.id,
          storyName: videoCreator.story?.title,
          userEmail: rootStore.userIdentityStore.currentIdentity?.email,
          userId: rootStore.userIdentityStore.currentIdentity?.id,
          fallbackModel: videoCreator.getStringFlag(
            'contentGenerationChatGptModel',
          ),
        }),
      }),
    [videoCreator, rootStore],
  );
}
